import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Save, Paperclip, ExternalLink, Mail } from "lucide-react";
import { API, api, errMsg, fromNow } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, SelectField } from "@/components/crm/fields";
import { StageAge } from "@/components/crm/bits";

const PURPOSES = [["introduction", "Introduction"], ["follow_up", "Follow-up"], ["requirement_clarification", "Requirement clarification"],
  ["quotation_follow_up", "Quotation follow-up"], ["sample_dispatch", "Sample dispatch"], ["thank_you", "Thank you / order confirmation"]];

function parseDraft(text) {
  const m = text.match(/^\s*Subject:\s*(.*)\n?/i);
  if (!m) return { subject: null, body: text };
  return { subject: m[1].trim(), body: text.slice(m[0].length).replace(/^\s*\n/, "") };
}

export default function EmailComposer({ open, onOpenChange, lead, draft, onSaved }) {
  const { settings } = useMeta();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ai, setAi] = useState({ purpose: "follow_up", tone: "professional", instructions: "" });
  const [aiGenerated, setAiGenerated] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [sent, setSent] = useState(null);
  const [acts, setActs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [attach, setAttach] = useState([]);
  const [gmail, setGmail] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSubject(draft?.subject || ""); setBody(draft?.body || ""); setDraftId(draft?.email_id || null);
    setAiGenerated(!!draft?.ai_generated); setSent(null); setAttach(draft?.attachment_ids || []);
    api.get(`/leads/${lead.lead_id}/activities`).then((r) => setActs(r.data.slice(0, 5))).catch(() => {});
    api.get(`/leads/${lead.lead_id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    api.get("/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft, lead?.lead_id]);

  const generate = async () => {
    setStreaming(true); setBody(""); let text = "";
    abortRef.current = new AbortController();
    try {
      const res = await fetch(`${API}/ai/leads/${lead.lead_id}/email-draft`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ai), signal: abortRef.current.signal });
      if (!res.ok) throw new Error((await res.json()).detail || "AI draft failed");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          const j = JSON.parse(d);
          if (j.error) throw new Error(j.error);
          text += j.t;
          const pd = parseDraft(text);
          if (pd.subject !== null) setSubject(pd.subject);
          setBody(pd.body);
        }
      }
      setAiGenerated(true);
    } catch (e) { if (e.name !== "AbortError") toast.error(e.message); } finally { setStreaming(false); }
  };

  const saveDraft = async (silent) => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return null; }
    try {
      const payload = { subject, body, ai_generated: aiGenerated, attachment_ids: attach };
      const r = draftId ? await api.patch(`/emails/${draftId}`, payload) : await api.post(`/leads/${lead.lead_id}/emails`, payload);
      setDraftId(r.data.email_id);
      if (!silent) toast.success("Draft saved");
      onSaved?.();
      return r.data.email_id;
    } catch (e) { toast.error(errMsg(e)); return null; }
  };

  const approve = async () => {
    const id = await saveDraft(true);
    if (!id) return;
    try {
      const r = await api.post(`/emails/${id}/approve-send`, { attachment_ids: attach });
      setSent(r.data); onSaved?.();
      toast.success(r.data.delivery === "gmail" ? `Sent from ${r.data.from}` : "Approved and logged against this lead");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const useTemplate = async () => {
    try { const r = await api.get(`/leads/${lead.lead_id}/journey-template`); setSubject(r.data.subject); setBody(r.data.body); setAiGenerated(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const viaGmail = gmail?.connected;

  const r = lead?.requirements || {};
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-4xl p-0 overflow-y-auto" data-testid="email-composer">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="font-display">New email</SheetTitle>
          <SheetDescription>Every email needs your approval before it goes out. Automatic sending is off.</SheetDescription>
        </SheetHeader>
        <div className="grid lg:grid-cols-[1fr_280px]">
          <div className="p-6 space-y-4 min-w-0">
            {sent?.delivery === "gmail" ? (
              <div className="rounded-xl border border-[#A3D9C9] bg-[#ECF5F1] p-5" data-testid="email-sent-gmail-panel">
                <p className="font-medium text-[#0B3C2D]">Sent from {sent.from}.</p>
                <p className="text-sm text-gray-600 mt-1">The email and its attachments are saved in this lead's history.</p>
              </div>
            ) : sent ? (
              <div className="rounded-xl border border-[#A3D9C9] bg-[#ECF5F1] p-5 space-y-3" data-testid="email-approved-panel">
                <p className="font-medium text-[#0B3C2D]">Email approved and saved to history.</p>
                <p className="text-sm text-gray-600">Open it in your business mailbox to send from your own address (attach files there if needed).</p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild className="bg-[#0B3C2D] hover:bg-[#072B20]" data-testid="open-gmail-button"><a href={sent.gmail_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open in Gmail</a></Button>
                  <Button asChild variant="outline" data-testid="open-mail-app-button"><a href={sent.mailto}><Mail className="h-4 w-4 mr-2" />Open in mail app</a></Button>
                </div>
              </div>
            ) : (<>
              <Field label="To"><Input data-testid="email-to" value={lead?.email || "No email on record"} disabled className="h-9" /></Field>
              <Field label="Subject"><Input data-testid="email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" /></Field>
              <Field label="Message">
                <Textarea data-testid="email-body-input" rows={14} value={body} onChange={(e) => setBody(e.target.value)} className="font-[Inter] leading-relaxed" placeholder="Write your message or generate a draft with AI →" />
              </Field>
              <div className="space-y-2" data-testid="email-attachments">
                <p className="text-xs font-medium text-gray-700 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />Attachments from this lead's documents</p>
                {docs.length === 0 ? <p className="text-xs text-gray-400">No documents uploaded yet (Quotations & Documents tab).</p> : (
                  <div className="flex flex-wrap gap-1.5">
                    {docs.map((d) => {
                      const on = attach.includes(d.document_id);
                      return <button key={d.document_id} type="button" data-testid={`attach-${d.document_id}`} onClick={() => setAttach(on ? attach.filter((x) => x !== d.document_id) : [...attach, d.document_id])}
                        className={`text-xs rounded-md px-2 py-1 ring-1 ${on ? "bg-[#ECF5F1] ring-[#A3D9C9] text-[#0B3C2D]" : "ring-gray-200 text-gray-600"}`}>{on ? "✓ " : ""}{d.filename}</button>;
                    })}
                  </div>
                )}
                {!viaGmail && <p className="text-xs text-gray-500" data-testid="gmail-not-connected-note">After approval this opens in your mailbox ready to send{attach.length ? " — attach the selected files there" : ""}.</p>}
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => saveDraft(false)} data-testid="email-save-draft"><Save className="h-4 w-4 mr-2" />Save draft</Button>
                <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" disabled={!lead?.email || streaming} onClick={() => setConfirm(true)} data-testid="email-send-button"><Send className="h-4 w-4 mr-2" />{viaGmail ? "Approve & send from Gmail" : "Approve & send"}</Button>
              </div>
            </>)}
          </div>
          <aside className="border-t lg:border-t-0 lg:border-l bg-[#FAFBFA] p-5 space-y-5">
            {!sent && (
              <Button variant="outline" className="w-full" onClick={useTemplate} data-testid="use-stage-template-button">Use {lead?.stage_name} template</Button>
            )}
            {settings?.ai?.enabled !== false && !sent && (
              <div className="space-y-3">
                <p className="eyebrow flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#0B3C2D]" />AI draft</p>
                <SelectField testid="ai-purpose-select" value={ai.purpose} onChange={(v) => setAi({ ...ai, purpose: v })} options={PURPOSES} allowNone={false} />
                <SelectField testid="ai-tone-select" value={ai.tone} onChange={(v) => setAi({ ...ai, tone: v })} options={[["professional", "Professional"], ["friendly", "Friendly"], ["concise", "Short & direct"], ["formal", "Formal"]]} allowNone={false} />
                <Textarea data-testid="ai-instructions-input" rows={3} placeholder="Optional: mention sample dispatch, ask for current bag spec…" value={ai.instructions} onChange={(e) => setAi({ ...ai, instructions: e.target.value })} />
                <Button variant="outline" className="w-full border-[#A3D9C9] text-[#0B3C2D]" disabled={streaming} onClick={generate} data-testid="ai-generate-button">
                  <Sparkles className="h-4 w-4 mr-2" />{streaming ? "Writing…" : body ? "Regenerate" : "Generate draft"}
                </Button>
              </div>
            )}
            <div className="space-y-2 text-sm">
              <p className="eyebrow">Customer context</p>
              <p className="font-medium">{lead?.company}</p>
              <p className="text-gray-500 text-xs">{lead?.contact_person} {lead?.designation && `· ${lead.designation}`}</p>
              <div className="flex flex-wrap gap-1.5 pt-1"><span className="text-xs rounded bg-white ring-1 ring-gray-200 px-1.5 py-0.5">{lead?.stage_name}</span>{lead && <StageAge lead={lead} />}</div>
              <dl className="text-xs text-gray-600 space-y-1 pt-2">
                {[["Product", r.product], ["Weight", r.fill_weight_kg && `${r.fill_weight_kg} kg`], ["Monthly", r.monthly_bags && `${Number(r.monthly_bags).toLocaleString("en-IN")} bags`], ["Bag type", r.bag_type]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2"><dt className="text-gray-400">{k}</dt><dd className="text-right">{v || "—"}</dd></div>
                ))}
              </dl>
            </div>
            <div className="space-y-2">
              <p className="eyebrow">Recent history</p>
              {acts.map((a) => <div key={a.activity_id} className="text-xs"><p className="text-gray-700 line-clamp-2">{a.content}</p><p className="text-gray-400">{fromNow(a.created_at)}</p></div>)}
            </div>
          </aside>
        </div>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve this email?</AlertDialogTitle>
              <AlertDialogDescription>
                {viaGmail ? `It will be sent now from ${gmail.email} to ${lead?.email}${attach.length ? ` with ${attach.length} attachment(s)` : ""}, and recorded in ${lead?.company}'s history.`
                  : `It will be recorded in ${lead?.company}'s history and opened in your mailbox ready to send to ${lead?.email}.`}
                {aiGenerated && " This draft was written with AI — please make sure you've reviewed it."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction data-testid="email-approve-confirm" className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={approve}>Approve</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
